import React, { useEffect, useMemo, useState } from "react";
import { C } from "../ipd-billing/constants";
import {
  fetchDiagnosticReview,
  peekDiagnosticReview,
} from "./diagnosticReportApi";

const EMPTY_REJECTION = { rejected: false, reason: "" };
const EMPTY_STATE = {
  signatories: [],
  rejection: EMPTY_REJECTION,
  publishedByPltId: {},
  submittedByPltId: {},
};

const idsFromKey = (idsKey) =>
  idsKey ? idsKey.split(",").map(Number).filter((n) => n > 0) : [];

function combineReviews(ids, reviews) {
  const byKey = new Map();
  reviews.forEach((r) => {
    (r.signatories || []).forEach((s) => {
      const key = s.id != null ? `id:${s.id}` : `name:${s.name}`;
      const prev = byKey.get(key);
      if (!prev || (!prev.image && s.image)) byKey.set(key, s);
    });
  });
  const rejected = reviews.find((r) => r.rejection?.rejected);
  const publishedMap = {};
  const submittedMap = {};
  ids.forEach((id, index) => {
    if (reviews[index]?.published) publishedMap[id] = true;
    if (reviews[index]?.submitted) submittedMap[id] = true;
  });
  return {
    signatories: [...byKey.values()],
    rejection: rejected?.rejection || EMPTY_REJECTION,
    publishedByPltId: publishedMap,
    submittedByPltId: submittedMap,
  };
}

export function useDiagnosticReview({
  patientLabTestId,
  extraIds = [],
  roleType,
} = {}) {
  const idsKey = useMemo(() => {
    const ids = [patientLabTestId, ...extraIds]
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    return [...new Set(ids)].join(",");
  }, [patientLabTestId, extraIds]);
  // When every id is already in the assignments read cache (the Order
  // Management click check just fetched it) the first render already carries
  // the signatures instead of painting without them until the effect runs.
  const [state, setState] = useState(() => {
    const ids = idsFromKey(idsKey);
    const reviews = ids.map((id) => peekDiagnosticReview(id));
    return ids.length > 0 && reviews.every(Boolean)
      ? combineReviews(ids, reviews)
      : EMPTY_STATE;
  });

  useEffect(() => {
    let cancelled = false;
    const ids = idsFromKey(idsKey);
    if (ids.length === 0) {
      setState(EMPTY_STATE);
      return undefined;
    }
    Promise.all(
      ids.map((id) => fetchDiagnosticReview(id, { roleType }))
    ).then((reviews) => {
      if (cancelled) return;
      setState(combineReviews(ids, reviews));
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey, roleType]);

  return state;
}

export function useDiagnosticSignatories(args) {
  return useDiagnosticReview(args).signatories;
}

/** On-screen signed pathologist / radiologist block for every report viewer. */
export default function DiagnosticSignatories({ signatories }) {
  if (!signatories?.length) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 28,
        flexWrap: "wrap",
        marginTop: 18,
        paddingTop: 12,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      {signatories.map((s) => (
        <div
          key={String(s.id || s.name)}
          style={{ textAlign: "center", minWidth: 130 }}
        >
          {s.image ? (
            <img
              src={s.image}
              alt=""
              style={{
                height: 48,
                maxWidth: 150,
                objectFit: "contain",
                display: "block",
                margin: "0 auto 6px",
              }}
            />
          ) : (
            <div
              style={{
                height: 48,
                borderBottom: `1px solid ${C.text}`,
                marginBottom: 6,
              }}
            />
          )}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
            {s.name || "Signed"}
          </div>
          <div style={{ fontSize: 10, color: C.textSub, marginTop: 2 }}>
            {s.roleLabel || "Consultant"}
          </div>
        </div>
      ))}
    </div>
  );
}
